"use client";

import { useEffect, useState } from "react";

export interface AsyncDataState<T> {
  data: T | undefined;
  loading: boolean;
  error: unknown;
}

interface UseAsyncDataOptions {
  /**
   * Skips the request while false. The last result for the same key is kept,
   * so re-enabling does not drop the caller back to a loading state.
   */
  enabled?: boolean;
}

interface InternalState<T> extends AsyncDataState<T> {
  key: string;
}

/**
 * Minimal react-query-shaped data hook for this package, which carries no
 * data-fetching dependency of its own.
 *
 * Runs `queryFunction` whenever `key` changes and tracks its data, loading and
 * error. A superseded or disabled run is aborted and never writes state, and a
 * key change clears the previous key's data rather than showing it under the
 * new one.
 *
 * `key` is serialised, so it holds JSON-serialisable values only.
 */
export function useAsyncData<T>(
  key: readonly unknown[],
  queryFunction: (signal: AbortSignal) => Promise<T>,
  { enabled = true }: UseAsyncDataOptions = {},
): AsyncDataState<T> {
  const serializedKey = JSON.stringify(key);
  const [state, setState] = useState<InternalState<T>>({
    key: serializedKey,
    data: undefined,
    loading: enabled,
    error: undefined,
  });

  useEffect(() => {
    if (!enabled) {
      setState((previous) =>
        previous.loading ? { ...previous, loading: false } : previous,
      );
      return;
    }

    const controller = new AbortController();

    setState((previous) =>
      previous.key === serializedKey
        ? { ...previous, loading: true }
        : {
            key: serializedKey,
            data: undefined,
            loading: true,
            error: undefined,
          },
    );

    const run = async () => {
      try {
        const data = await queryFunction(controller.signal);
        if (controller.signal.aborted) return;
        setState({
          key: serializedKey,
          data,
          loading: false,
          error: undefined,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState((previous) => ({
          key: serializedKey,
          data: previous.key === serializedKey ? previous.data : undefined,
          loading: false,
          error,
        }));
      }
    };

    void run();

    return () => controller.abort();
    // `queryFunction` is re-created every render; `key` is the refetch trigger,
    // the same contract react-query gives `queryKey`/`queryFn`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, serializedKey]);

  // The effect has not run yet for a key that changed this render, so the state
  // it holds still describes the previous key.
  if (state.key !== serializedKey) {
    return { data: undefined, loading: enabled, error: undefined };
  }

  return { data: state.data, loading: state.loading, error: state.error };
}
